import { IdentificationContainer } from '../identification';
import { PersonalInformationFields } from '../personal-information';
import { Flex } from '@servicetitan/anvil2';
import { ApplicationData, ApplicationDataStore } from '../application/ApplicationDataStore';
import { createSchema } from './schema';
import { SaveFormDataToStore } from '../application/SaveFormDataToStore';
import { Form, FormProps } from '../common/Form';
import { useMemo } from 'react';
import { deleteUndefinedKeys } from '@servicetitan/financing-utils-shared';
import {
    useProviderConfig,
    useSecondLookIntegration,
} from '@servicetitan/financing-workflow-shared';
import { JobDetailsFields } from '../job-details/JobDetailsFields';

export const Container = (
    props: Omit<
        FormProps<ApplicationData['applicant']>,
        'schema' | 'defaultValues' | 'formData' | 'children'
    >,
) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const config = useProviderConfig();
    const secondLookIntegration = useSecondLookIntegration();
    // Keep backward compatibility check for identification and jobDetails section visibility
    const { additionalApplicantFields = [] } = config;
    const requiresIdentification = additionalApplicantFields.includes('identification');
    const requiresJobDetails = additionalApplicantFields.includes('jobDetails');

    const { secondLookProviderConfig } = secondLookIntegration;

    // Get only the default values from the application data and cache the result
    // so we aren't sending all the application data to the form on every change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const applicantData = useMemo(() => deleteUndefinedKeys(applicationData.applicant), []);

    const schema = useMemo(() => {
        // When a waterfall second-look config is present, use it to widen the residence value set.
        // When the current provider is itself a second-look provider (e.g. Finturf applied to
        // directly via the prequals banner), fall back to its own fieldValueSets so its extended
        // residence options are accepted by validation.
        return createSchema({
            ...config,
            secondLookProviderConfig: secondLookProviderConfig ?? config,
        });
    }, [config, secondLookProviderConfig]);

    return (
        <Form {...props} formData={applicantData} schema={schema}>
            <SaveFormDataToStore fieldPrefix="applicant" />
            <Flex direction="column" gap="10">
                <PersonalInformationFields fieldPrefix="primaryApplicant" />
                {requiresIdentification && (
                    <IdentificationContainer fieldPrefix="primaryApplicant" />
                )}
                {requiresJobDetails && <JobDetailsFields fieldPrefix="primaryApplicant" />}
            </Flex>
        </Form>
    );
};
